import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/utils'
import { CompanyLogo } from './CompanyLogo'

/**
 * The customer's own name and mark, shown once they are signed in.
 *
 * Livprom's logo used to sit in the sign-in footer, which was right while this
 * was one company's internal tool. It is wrong now: the sign-in screen is
 * reached before anyone has said who they are, so on a shared host any company
 * branding there would be a guess — and usually the wrong one.
 *
 * Here the account is known, so the branding is a fact rather than an
 * assumption. It lives in the sidebar header, under the product's own mark:
 * whose factory this is, under whose software it is, in one column.
 *
 * ## The logo is a URL, not an upload
 *
 * Customers supply a link to their own mark rather than a file this application
 * stores. That keeps branding entirely out of the upload path: no new storage,
 * no image validation, no signed URLs, and nothing one customer can serve into
 * another customer's page. A broken or missing link degrades to the company
 * name, which is the information that actually matters.
 *
 * Rendering, including the dark-theme problem, is CompanyLogo's job.
 */
export function CompanyBrand({
  className,
  showName = true,
}: {
  className?: string
  showName?: boolean
}) {
  const { user } = useAuth()

  const company = user?.company

  if (!company?.name) return null

  /*
   * Nothing to draw, so draw nothing.
   *
   * With the name suppressed and no logo set, this would otherwise render an
   * empty span — and its caller in the sidebar wraps it in a bordered block,
   * leaving a rule floating above the account row with nothing under it. A
   * component that renders nothing should say so rather than leave its caller
   * to guess.
   */
  if (!showName && !company.logoUrl) return null

  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <CompanyLogo
        src={company.logoUrl}
        darkSrc={company.logoUrlDark}
        name={company.name}
        fallback="none"
      />

      {showName && <span className="truncate text-sm font-medium">{company.name}</span>}
    </span>
  )
}
